import { zodResolver } from '@hookform/resolvers/zod'
import { ResetPasswordSchema, resetPasswordSchema } from '@longy/clinic-schema'
import { DotTranslationKey } from '@longy/common'
import {
    authPillButtonClass,
    Button,
    Form,
    FormPasswordField,
    Icons,
    useToast,
} from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { getRouteApi } from '@tanstack/react-router'
import i18n from 'i18next'
import { Check } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AuthLayout } from '../../components/auth/AuthLayout'
import { api, isTRPCClientError } from '../../core/trpc'

const PATH = '/reset-password/$resetToken'
const routeApi = getRouteApi(PATH)

const ResetPasswordPage = () => {
    const { t } = useTranslation()
    const { toast } = useToast()

    const { resetToken } = routeApi.useParams()

    const [passwordReset, setPasswordReset] = useState<boolean>(false)

    const form = useForm<ResetPasswordSchema>({
        resolver: zodResolver(resetPasswordSchema),
        defaultValues: {
            resetToken,
            password: '',
            confirmPassword: '',
        },
    })

    const { handleSubmit, control } = form

    const { mutateAsync: resetPassword, isPending } = api.user.resetPassword.useMutation()

    const onSubmit = async (data: ResetPasswordSchema) => {
        try {
            const { jwt } = await resetPassword(data)
            if (jwt) {
                toast({
                    description: t('app.passwordReset.passwordWasReset'),
                    variant: 'success',
                })

                setPasswordReset(true)
            } else {
                toast({
                    description: t('common.errors.defaultError'),
                    variant: 'destructive',
                })
            }
        } catch (error) {
            toast({
                description: isTRPCClientError(error)
                    ? i18n.exists(error.message)
                        ? t(error.message as DotTranslationKey)
                        : error.message
                    : t('common.errors.defaultError'),
                variant: 'destructive',
            })
        }
    }

    return (
        <AuthLayout title={t('common.auth.resetPassword')}>
            {!passwordReset ? (
                <>
                    <Form {...form}>
                        <FormPasswordField
                            disabled={isPending || passwordReset}
                            control={control}
                            label={t('common.auth.password')}
                            name="password"
                            inputProps={{
                                placeholder: '********',
                                disabled: isPending,
                            }}
                        />
                        <FormPasswordField
                            disabled={isPending || passwordReset}
                            control={control}
                            label={t('common.auth.confirmPassword')}
                            name="confirmPassword"
                            inputProps={{
                                placeholder: '********',
                                disabled: isPending,
                            }}
                        />
                    </Form>
                    <Button
                        className={authPillButtonClass}
                        disabled={isPending || passwordReset}
                        onClick={handleSubmit(onSubmit)}
                    >
                        {isPending && <Icons.Spinner className="mr-2 h-4 animate-spin" />}
                        {t('common.app.save')}
                    </Button>
                </>
            ) : (
                <div className="flex flex-col items-center gap-4">
                    <Check className="size-12 text-primary" />
                    <p className="text-center text-lg font-semibold">
                        {t('app.passwordReset.passwordWasReset')}
                    </p>
                    {/*
                     * The mutation hands back a session token, but it is deliberately
                     * not used to sign the user in: `user.login` requires a second
                     * factor for accounts that have one, and consuming the token here
                     * would let a reset link skip it.
                     */}
                    <Link className="text-primary underline" to="/login">
                        {t('frontend.auth.login')}
                    </Link>
                </div>
            )}
        </AuthLayout>
    )
}

export const Route = createFileRoute(PATH)({
    head: () => ({
        meta: [{ title: 'Long-Y - Jelszó visszaállítás' }],
    }),
    component: ResetPasswordPage,
})
